import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckSquare, Download, HandCoins, ListPlus, MapPin, Package, Send, Truck, Upload } from 'lucide-react';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { downloadSelectedLabels, fulfillDeliveries } from '../../features/deliveries/deliveriesThunks';

const FULFILL_CONSEQUENCES = [
  'Pasan a Preparado los de tu operación que están Pendiente, Creado en courier, Retirado o Preparado; el resto queda como está.',
  'Se avisa a la tienda (Shopify, VTEX o PrestaShop), al TMS y a tus webhooks.',
  'No se puede deshacer.',
];

/**
 * What can be done with the rows the deliveries history has selected, for the
 * three profiles: each page says which actions its profile gets.
 *
 * A bar of its own, above the table. One action leads (preparing), sending to
 * the courier is the alternative, and the rest are utilities — the hierarchy of
 * the button guideline in the README. What each action did is said by its
 * toast (see `deliveriesFeedback`); this bar only starts them.
 *
 * @param {object} props
 * @param {object} props.user
 * @param {number[]} props.selectedRows - Delivery ids, what the table selects.
 * @param {number[]} props.selectedOrderIds - Their order ids, what most bulk
 *   endpoints take.
 * @param {boolean} [props.canChangeStatus]
 * @param {boolean} [props.canAssignDriver]
 * @param {boolean} [props.canCede]
 * @param {boolean} [props.canDispatch]
 * @param {boolean} [props.canChangeCourier]
 * @param {string|null} [props.changeCourierBlockedBy] - Why «Cambiar courier» is
 *   shown off, e.g. the staff with no operator picked; `null` when it can run.
 * @param {boolean} [props.canChangePickupPoint] - The operator has the pickup
 *   points addon: its shipments can go to one of its points.
 * @param {() => void} [props.onOpenChangeStatusModal]
 * @param {() => void} [props.onOpenChangeDriverModal]
 * @param {() => void} [props.onOpenUpsellModal]
 * @param {() => void} [props.onOpenDispatchModal]
 * @param {() => void} [props.onOpenChangeCourierModal]
 * @param {() => void} [props.onOpenChangePickupPointModal]
 * @param {() => void} [props.onFulfilled] - After preparing went through, to
 *   refresh the table and clear the selection.
 */
export const SelectionActions = ({
  user,
  selectedRows,
  selectedOrderIds = [],
  canChangeStatus = false,
  canAssignDriver = false,
  canCede = false,
  canDispatch = false,
  canChangeCourier = false,
  changeCourierBlockedBy = null,
  canChangePickupPoint = false,
  onOpenChangeStatusModal,
  onOpenChangeDriverModal,
  onOpenUpsellModal,
  onOpenDispatchModal,
  onOpenChangeCourierModal,
  onOpenChangePickupPointModal,
  onFulfilled,
}) => {
  const dispatch = useDispatch();
  const { isFulfilling, isDownloadingLabels } = useSelector((state) => state.deliveries);
  const [confirmingFulfill, setConfirmingFulfill] = useState(false);

  if (selectedRows.length === 0) return null;

  const orders = selectedOrderIds.length;

  const handleFulfill = async () => {
    const result = await dispatch(fulfillDeliveries({ ids: selectedOrderIds }));
    setConfirmingFulfill(false);
    // Unanswered may still have gone through: the refresh shows which.
    if (fulfillDeliveries.fulfilled.match(result) || result.meta?.unconfirmed) onFulfilled?.();
  };

  return (
    <div
      data-testid="delivery-bulk-actions"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 rounded-xl border border-border bg-white dark:bg-[#1a1a1a] animate-in fade-in duration-300"
    >
      <div className="flex items-center gap-1.5 text-ninja-green font-bold text-xs whitespace-nowrap">
        <Package size={14} />
        <span>
          {selectedRows.length}{' '}
          {selectedRows.length === 1 ? 'seleccionada' : 'seleccionadas'}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          variant="ghost"
          size="xs"
          icon={Download}
          loading={isDownloadingLabels}
          onClick={() => dispatch(downloadSelectedLabels({ ids: selectedOrderIds, user }))}
        >
          Etiquetas
        </Button>
        {canAssignDriver && (
          <Button variant="ghost" size="xs" icon={ListPlus} onClick={onOpenChangeDriverModal}>
            Asignar conductor
          </Button>
        )}
        {canChangeStatus && (
          <Button variant="ghost" size="xs" icon={Upload} onClick={onOpenChangeStatusModal}>
            Modificar envíos
          </Button>
        )}
        {canCede && (
          <Button variant="ghost" size="xs" icon={HandCoins} onClick={onOpenUpsellModal}>
            Ceder a partner
          </Button>
        )}
        {canChangeCourier && (
          <Button
            variant="ghost"
            size="xs"
            icon={Truck}
            onClick={onOpenChangeCourierModal}
            disabled={Boolean(changeCourierBlockedBy)}
            title={changeCourierBlockedBy || undefined}
            data-testid="deliveries-change-courier"
          >
            Cambiar courier
          </Button>
        )}
        {canChangePickupPoint && (
          <Button
            variant="ghost"
            size="xs"
            icon={MapPin}
            onClick={onOpenChangePickupPointModal}
            data-testid="deliveries-change-pickup-point"
          >
            Punto de retiro
          </Button>
        )}
        {canDispatch && (
          <Button
            variant="outline"
            size="xs"
            icon={Send}
            onClick={onOpenDispatchModal}
            data-testid="deliveries-dispatch-courier"
          >
            Enviar al courier
          </Button>
        )}
        <Button
          variant="primary"
          size="xs"
          icon={CheckSquare}
          loading={isFulfilling}
          onClick={() => setConfirmingFulfill(true)}
          disabled={orders === 0}
        >
          Preparar envíos
        </Button>
      </div>

      <ConfirmDialog
        isOpen={confirmingFulfill}
        onClose={() => setConfirmingFulfill(false)}
        onConfirm={handleFulfill}
        variant="primary"
        title={`Preparar ${orders === 1 ? 'el envío' : `${orders} envíos`}`}
        message="Quedan marcados como Preparados, con todos sus bultos."
        consequences={FULFILL_CONSEQUENCES}
        confirmLabel={`Preparar ${orders} ${orders === 1 ? 'envío' : 'envíos'}`}
        loadingLabel="Preparando..."
        loading={isFulfilling}
        testId="deliveries-fulfill-dialog"
      />
    </div>
  );
};
